import { AxiosResponse } from 'axios';
import { apiService } from './ApiService';

// A term maps to an ORDERED list of meanings (catalog part-name keywords); the
// order is the priority — rank 1 (index 0) is the strongest match.
export interface KnowledgePriority {
    id: number;
    catalog: string;
    language: string;
    organization_id?: number | null;  // null = global (all orgs)
    term: string;
    meanings: string[];  // ordered; index 0 == rank 1 (highest priority)
    note?: string | null;
    enabled: boolean;
    created_by?: string | null;
    created_at: string;
    updated_at: string;
}

export type KnowledgePriorityInput = Omit<
    KnowledgePriority,
    'id' | 'created_by' | 'created_at' | 'updated_at'
>;

export interface KnowledgePriorityListResponse {
    status: string;
    priorities: KnowledgePriority[];
    total_count: number;
}

export interface KnowledgeImportResult {
    status: string;
    created: number;
    updated: number;
    skipped: number;
    errors: string[];
    total: number;
}

const BASE = '/api/admin/knowledge-priorities';

export class KnowledgePrioritiesService {
    private static instance: KnowledgePrioritiesService;

    public static getInstance(): KnowledgePrioritiesService {
        if (!KnowledgePrioritiesService.instance) {
            KnowledgePrioritiesService.instance = new KnowledgePrioritiesService();
        }
        return KnowledgePrioritiesService.instance;
    }

    public async list(params?: {
        catalog?: string;
        language?: string;
        term?: string;
        organization_id?: number;
        global_only?: boolean;
        limit?: number;
        offset?: number;
    }): Promise<AxiosResponse<KnowledgePriorityListResponse>> {
        return apiService.get(BASE, { params });
    }

    public async create(data: KnowledgePriorityInput): Promise<AxiosResponse<KnowledgePriority>> {
        return apiService.post(BASE, data);
    }

    public async update(
        id: number,
        data: Partial<KnowledgePriorityInput>,
    ): Promise<AxiosResponse<KnowledgePriority>> {
        return apiService.put(`${BASE}/${id}`, data);
    }

    public async remove(id: number): Promise<AxiosResponse<any>> {
        return apiService.delete(`${BASE}/${id}`);
    }

    /** Bulk-load priorities from a YAML file (seed_taxonomy.yaml format). */
    public async importYaml(
        file: File,
        overwrite = false,
    ): Promise<AxiosResponse<KnowledgeImportResult>> {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('overwrite', String(overwrite));
        return apiService.post(`${BASE}/import`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }
}

export const knowledgePrioritiesService = KnowledgePrioritiesService.getInstance();
